import React from 'react'
import { inputStyles } from './shared/FormStyles'
import { toast } from 'sonner'
import { navigatetoHome } from '@/utils/footer-navigations'

const StampinLogoSection = ({ settings, onStyleChange, plan }) => {
  if (plan !== 'enterprise') {
    return (
      <div className="mt-6 p-4 bg-gray-50 border rounded-lg">
        <div className="flex items-start space-x-3">
          <span className="text-gray-500 text-xl">🔒</span>
          <div>
            <h4 className="font-medium text-gray-900">Stampin Logo</h4>
            <p className="text-sm text-gray-600 mt-1">
              The Stampin logo is removable only for Enterprise plan users.
            </p>
            <button
              className="mt-3 px-4 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-full 
                                   hover:bg-blue-600 transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                navigatetoHome('/#prices')
              }}
            >
              Upgrade Plan
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium">Stampin Logo</h3>
        {plan === 'enterprise' && (
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.visible}
              onChange={(e) => onStyleChange({ visible: e.target.checked })}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">
              {settings.visible ? 'Visible' : 'Hidden'}
            </span>
          </label>
        )}
      </div>
    </div>
  )
}

const LogoForm = ({
  onFileChange,
  logoStyle,
  onStyleChange,
  stampinLogoSettings,
  onStampinLogoChange,
  plan,
}) => {
  const validateImage = (file) => {
    const maxSize = 20 * 1024 * 1024 // 20MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
    const allowedExtensions = ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg']

    if (!file) {
      toast.error('File is required')
      return false
    }

    if (!allowedTypes.includes(file.type)) {
      toast.error(`Only ${allowedExtensions.join(', ')} files are allowed`)
      return false
    }

    if (file.size >= maxSize) {
      toast.error(`File size must be less than ${maxSize / (1024 * 1024)}MB`)
      return false
    }

    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(`Invalid file extension. Allowed: ${allowedExtensions.join(', ')}`)
      return false
    }

    return true
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file && validateImage(file)) {
      onFileChange(file)
    }
  }

  return (
    <div className={inputStyles.container}>
      {/* Business Logo Section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium mb-4">Business Logo</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onStyleChange({ image: '' })}
              className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 
                 border border-red-200 rounded-md hover:bg-red-50 
                 transition-colors"
              type="button"
            >
              Remove Logo
            </button>
          </div>
        </div>

        <div>
          <span className={inputStyles.label}>Upload Logo</span>
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
            />
            <div className="px-4 py-2 border rounded-md bg-white text-gray-600 hover:bg-gray-50">
              Choose Logo
            </div>
          </div>

          {/* File Requirements Card */}
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <span className="text-red-500 text-xl">⚠️</span>
              <div>
                <h4 className="font-medium text-red-800 mb-1">File Limitations</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li className="flex items-center space-x-2">
                    <span>📁</span>
                    <span>Supported formats: JPEG, PNG, GIF, WebP, SVG</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span>📊</span>
                    <span>Maximum size: 20MB</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Best Practices Tip Card */}
          <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <span className="text-amber-500 text-xl">💡</span>
              <div>
                <h4 className="font-medium text-amber-800 mb-1">Pro Tips</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li className="flex items-center space-x-2">
                    <span>🎯</span>
                    <span>Recommended size: 60x60 pixels</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span>✨</span>
                    <span>Use transparent PNG for best results</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <label className="block">
            <span className={inputStyles.label}>Logo Size</span>
            <input
              type="range"
              min="50"
              max="200"
              value={logoStyle.width}
              onChange={(e) =>
                onStyleChange({
                  width: parseInt(e.target.value),
                  height: parseInt(e.target.value),
                })
              }
              className="w-full"
            />
          </label>
        </div>

        <div className="mt-4">
          <label className="block">
            <span className={inputStyles.label}>Border Radius</span>
            <input
              type="range"
              min="0"
              max="50"
              value={logoStyle.borderRadius}
              onChange={(e) => onStyleChange({ borderRadius: parseInt(e.target.value) })}
              className="w-full"
            />
          </label>
        </div>
      </div>

      {/* Stampin Logo Section */}
      <hr />
      <StampinLogoSection
        settings={stampinLogoSettings}
        onStyleChange={onStampinLogoChange}
        plan={plan}
      />
    </div>
  )
}

export default LogoForm
